/**
 * Componente de Avatar basado en Lottie
 * Soporta múltiples avatares y estados (idle, greeting, talking)
 */

import { useEffect, useRef, useState } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { useAvatarAnimation } from '../../hooks/useAvatarAnimation';
import { PracticeMode } from '../../types';
import './LottieAvatar.css';

interface LottieAvatarProps {
  className?: string;
  mode?: PracticeMode;
  preview?: boolean;
  avatarId?: string; // 'avatar1', 'avatar2', etc.
}

export function LottieAvatar({ 
  className, 
  mode, 
  preview = false, 
  avatarId = 'avatar1' 
}: LottieAvatarProps) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const { currentState } = useAvatarAnimation();
  const [animationData, setAnimationData] = useState<any>(null);
  const [currentAnimationState, setCurrentAnimationState] = useState<string>('idle');
  const [shouldLoop, setShouldLoop] = useState(true);
  const [shouldPlay, setShouldPlay] = useState(true);

  // Mapear estado de avatar a archivo de animación
  const getAnimationFile = (state: string): string => {
    switch (state) {
      case 'greeting':
        return 'greeting.json';
      case 'talking':
        return 'talking.json';
      case 'idle':
      default:
        return 'idle.json';
    }
  };

  // Cargar animación cuando cambia el estado o en el montaje inicial
  useEffect(() => {
    const animFile = getAnimationFile(currentState);
    const animPath = `/avatars/${avatarId}/${animFile}`;

    // Cargar si: cambió el estado O es la primera carga (sin datos aún)
    if (currentAnimationState !== currentState || !animationData) {
      // Configurar loop según el estado antes de cargar
      if (currentState === 'greeting') {
        setShouldLoop(false);
      } else {
        setShouldLoop(true);
      }
      setShouldPlay(true);

      fetch(animPath)
        .then(response => {
          if (!response.ok) {
            throw new Error(`No se pudo cargar la animación: ${animPath}`);
          }
          return response.json();
        })
        .then(data => {
          setAnimationData(data);
          setCurrentAnimationState(currentState);
        })
        .catch(error => {
          console.error('Error al cargar animación Lottie:', error);
          // Fallback a idle si hay error
          if (currentState !== 'idle') {
            fetch(`/avatars/${avatarId}/idle.json`)
              .then(res => res.json())
              .then(data => setAnimationData(data))
              .catch(err => console.error('Error al cargar fallback idle:', err));
          }
        });
    }
  }, [currentState, avatarId, currentAnimationState]);

  if (!animationData) {
    return (
      <div className={`lottie-avatar-container loading ${preview ? 'preview' : ''} ${className || ''}`}>
        <div className="loading-spinner">Cargando avatar...</div>
      </div>
    );
  }

  return (
    <div 
      className={`lottie-avatar-container ${preview ? 'preview' : ''} ${className || ''}`}
      aria-label="Avatar"
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={shouldLoop}
        autoplay={shouldPlay}
        style={{
          width: '100%',
          height: '100%',
          maxWidth: preview ? '200px' : '320px',
          margin: '0 auto',
        }}
      />
    </div>
  );
}

// Exportar función para controlar desde otros componentes
export { useAvatarAnimation };
